import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import type { ListSingleUseTokensResponse, SingleUseTokenListItem } from '@pillar-portal/api-types';

type TokenActionFn = (singleUseAuthTokenUuid: string) => Promise<{ message: string }>;
type PaymentRequestActionFn = (
    paymentRequestUuid: string,
    merchantOrganizationUuid: string
) => Promise<{ message: string }>;
type TokenFetchFn = (outstandingOnly: boolean) => Promise<ListSingleUseTokensResponse>;

const usdCurrencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const ResendableSingleUseTokenTypes: ReadonlySet<SingleUseTokenListItem['type']> = new Set([
    'INVITATION',
    'PASSWORD_RESET',
    'EMAIL_VERIFICATION',
    'SMS_VERIFICATION',
]);

interface SingleUseTokensCardProps {
    title: string;
    queryKey: ReadonlyArray<unknown>;
    fetchTokens: TokenFetchFn;
    expireToken: TokenActionFn;
    revokePaymentRequestLink?: PaymentRequestActionFn;
    resendToken: TokenActionFn;
    emptyOutstandingMessage: string;
    emptyHistoricalMessage: string;
    showUserColumns: boolean;
}

export const canResendSingleUseToken = (token: SingleUseTokenListItem): boolean => {
    return token.used_ts === null && ResendableSingleUseTokenTypes.has(token.type);
};

export function formatSingleUseTokenType(tokenType: SingleUseTokenListItem['type']): string {
    if (tokenType === 'PILLAR_LOGIN_LINK') {
        return 'Anchorbase Portal login link';
    }
    return tokenType;
}

export function formatSingleUseTokenTimestamp(timestampIso: string | null): string {
    if (timestampIso === null) {
        return '-';
    }
    return new Date(timestampIso).toLocaleString();
}

export function isEInvoiceSingleUseToken(token: SingleUseTokenListItem): boolean {
    return token.type === 'PAYMENT_REQUEST_ACCESS' && token.payment_request !== undefined;
}

export function formatSingleUseTokenAmount(
    amount: number | undefined,
    currencyCode: string | undefined
): string {
    if (amount === undefined) {
        return '—';
    }
    if (currencyCode === 'USD') {
        return usdCurrencyFormatter.format(amount);
    }
    if (currencyCode === undefined) {
        return amount.toFixed(2);
    }
    return `${amount.toFixed(2)} ${currencyCode}`;
}

export function formatSingleUseTokenOrganizations(token: SingleUseTokenListItem): string {
    if (token.organizations.length === 0) {
        return '-';
    }
    return token.organizations.map((organization) => organization.name).join(', ');
}

export function formatSingleUseTokenStatus(token: SingleUseTokenListItem): string {
    if (token.is_outstanding) {
        return 'Outstanding';
    }
    if (token.used_ts !== null) {
        return 'Used';
    }
    if (token.is_expired) {
        return 'Expired';
    }
    return 'Unknown';
}

export function formatPaymentRequestStatus(token: SingleUseTokenListItem): string {
    return token.payment_request?.status ?? '-';
}

export function renderSingleUseTokenOrganizations(
    token: SingleUseTokenListItem,
    variant: SingleUseTokensTableVariant
): ReactNode {
    if (token.organizations.length === 0) {
        return '-';
    }

    return token.organizations.map((organization, index) => {
        const separator: string = index < token.organizations.length - 1 ? ', ' : '';

        if (variant !== 'e-invoice') {
            return (
                <span key={organization.organization_uuid}>
                    {organization.name}
                    {separator}
                </span>
            );
        }

        return (
            <span key={organization.organization_uuid}>
                <Link
                    className="text-primary underline-offset-4 hover:underline"
                    to={`/organizations/${organization.organization_uuid}`}
                >
                    {organization.name}
                </Link>
                {separator}
            </span>
        );
    });
}

type SingleUseTokensTableVariant = 'user-management' | 'e-invoice';

interface SingleUseTokensTableProps {
    tokens: ReadonlyArray<SingleUseTokenListItem>;
    showUserColumns: boolean;
    isBusy: boolean;
    onExpire: (singleUseAuthTokenUuid: string) => void;
    onRevokePaymentRequest: (
        paymentRequestUuid: string,
        merchantOrganizationUuid: string
    ) => void;
    onResend: (singleUseAuthTokenUuid: string) => void;
    variant?: SingleUseTokensTableVariant;
}

export function SingleUseTokensTable(props: SingleUseTokensTableProps) {
    const variant: SingleUseTokensTableVariant = props.variant ?? 'user-management';

    return (
        <div className="rounded-md border">
            <Table>
                <TableHeader>
                    <TableRow>
                        {variant === 'user-management' ? <TableHead>Type</TableHead> : null}
                        {variant === 'user-management' && props.showUserColumns ? (
                            <TableHead>User</TableHead>
                        ) : null}
                        <TableHead>Organization</TableHead>
                        {variant === 'e-invoice' ? <TableHead>Customer</TableHead> : null}
                        <TableHead>Email</TableHead>
                        {variant === 'e-invoice' ? <TableHead>Phone</TableHead> : null}
                        {variant === 'e-invoice' ? <TableHead>Amount</TableHead> : null}
                        <TableHead>Created</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead>Used</TableHead>
                        <TableHead>Token Status</TableHead>
                        {variant === 'e-invoice' ? <TableHead>Payment Status</TableHead> : null}
                        <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {props.tokens.map((token) => {
                        const paymentRequestUuid = token.payment_request?.payment_request_uuid;
                        const merchantOrganizationUuid =
                            token.payment_request?.merchant_organization_uuid;

                        return (
                            <TableRow key={token.single_use_auth_token_uuid}>
                                {variant === 'user-management' ? (
                                    <TableCell>{formatSingleUseTokenType(token.type)}</TableCell>
                                ) : null}
                                {variant === 'user-management' && props.showUserColumns ? (
                                    <TableCell>
                                        {token.user_primary_email !== null
                                            ? token.user_primary_email
                                            : '-'}
                                    </TableCell>
                                ) : null}
                                <TableCell>
                                    {renderSingleUseTokenOrganizations(token, variant)}
                                </TableCell>
                                {variant === 'e-invoice' ? (
                                    <TableCell>
                                        {token.payment_request?.customer_name ?? '-'}
                                    </TableCell>
                                ) : null}
                                <TableCell>{token.email}</TableCell>
                                {variant === 'e-invoice' ? (
                                    <TableCell>
                                        {token.payment_request?.recipient_phone ?? '-'}
                                    </TableCell>
                                ) : null}
                                {variant === 'e-invoice' ? (
                                    <TableCell>
                                        {formatSingleUseTokenAmount(
                                            token.payment_request?.original_amount,
                                            token.payment_request?.currency_code
                                        )}
                                    </TableCell>
                                ) : null}
                                <TableCell>
                                    {formatSingleUseTokenTimestamp(token.created_ts)}
                                </TableCell>
                                <TableCell>
                                    {formatSingleUseTokenTimestamp(token.expire_ts)}
                                </TableCell>
                                <TableCell>
                                    {formatSingleUseTokenTimestamp(token.used_ts)}
                                </TableCell>
                                <TableCell>
                                    {formatSingleUseTokenStatus(token)}
                                </TableCell>
                                {variant === 'e-invoice' ? (
                                    <TableCell>{formatPaymentRequestStatus(token)}</TableCell>
                                ) : null}
                                <TableCell className="text-right space-x-2">
                                    {variant === 'e-invoice' &&
                                    paymentRequestUuid !== undefined &&
                                    merchantOrganizationUuid !== undefined ? (
                                        <>
                                            <Button size="sm" variant="outline" asChild>
                                                <Link
                                                    to={`/payment-requests/${paymentRequestUuid}?merchant_organization_uuid=${merchantOrganizationUuid}`}
                                                >
                                                    Details
                                                </Link>
                                            </Button>
                                        </>
                                    ) : null}
                                    {variant !== 'e-invoice' && token.is_outstanding && (
                                        <Button
                                            size="sm"
                                            variant="destructive"
                                            disabled={props.isBusy}
                                            onClick={() =>
                                                props.onExpire(token.single_use_auth_token_uuid)
                                            }
                                        >
                                            Expire
                                        </Button>
                                    )}
                                    {canResendSingleUseToken(token) && (
                                        <Button
                                            size="sm"
                                            variant="default"
                                            disabled={props.isBusy}
                                            onClick={() =>
                                                props.onResend(token.single_use_auth_token_uuid)
                                            }
                                        >
                                            Resend
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
}

export function SingleUseTokensCard(props: SingleUseTokensCardProps) {
    const queryClient = useQueryClient();
    const [showAllHistoricalTokens, setShowAllHistoricalTokens] = useState(false);
    const [operationMessage, setOperationMessage] = useState<string | null>(null);
    const [operationErrorMessage, setOperationErrorMessage] = useState<string | null>(null);
    const outstandingOnly: boolean = !showAllHistoricalTokens;

    const tokensQuery = useQuery({
        queryKey: [...props.queryKey, { outstandingOnly }],
        queryFn: async () => {
            return await props.fetchTokens(outstandingOnly);
        },
    });

    const expireMutation = useMutation({
        mutationFn: async (singleUseAuthTokenUuid: string) => {
            return await props.expireToken(singleUseAuthTokenUuid);
        },
        onSuccess: async (response) => {
            setOperationErrorMessage(null);
            setOperationMessage(response.message);
            await queryClient.invalidateQueries({ queryKey: props.queryKey });
        },
        onError: (error: unknown) => {
            setOperationMessage(null);
            if (error instanceof Error) {
                setOperationErrorMessage(error.message);
            } else {
                setOperationErrorMessage('Failed to expire token');
            }
        },
    });

    const resendMutation = useMutation({
        mutationFn: async (singleUseAuthTokenUuid: string) => {
            return await props.resendToken(singleUseAuthTokenUuid);
        },
        onSuccess: async (response) => {
            setOperationErrorMessage(null);
            setOperationMessage(response.message);
            await queryClient.invalidateQueries({ queryKey: props.queryKey });
        },
        onError: (error: unknown) => {
            setOperationMessage(null);
            if (error instanceof Error) {
                setOperationErrorMessage(error.message);
            } else {
                setOperationErrorMessage('Failed to resend token');
            }
        },
    });

    const tokens: ReadonlyArray<SingleUseTokenListItem> = tokensQuery.data?.tokens ?? [];

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{props.title}</CardTitle>
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="showAllSingleUseTokens"
                        checked={showAllHistoricalTokens}
                        onCheckedChange={(checked) => setShowAllHistoricalTokens(checked === true)}
                    />
                    <Label htmlFor="showAllSingleUseTokens">Show all historical tokens</Label>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {operationMessage !== null && (
                    <Alert>
                        <AlertDescription>{operationMessage}</AlertDescription>
                    </Alert>
                )}
                {operationErrorMessage !== null && (
                    <Alert variant="destructive">
                        <AlertDescription>{operationErrorMessage}</AlertDescription>
                    </Alert>
                )}
                {tokensQuery.isLoading ? (
                    <p className="text-sm text-muted-foreground">Loading tokens...</p>
                ) : tokens.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        {outstandingOnly
                            ? props.emptyOutstandingMessage
                            : props.emptyHistoricalMessage}
                    </p>
                ) : (
                    <SingleUseTokensTable
                        tokens={tokens}
                        showUserColumns={props.showUserColumns}
                        isBusy={expireMutation.isPending || resendMutation.isPending}
                        variant="user-management"
                        onExpire={(singleUseAuthTokenUuid) => {
                            expireMutation.mutate(singleUseAuthTokenUuid);
                        }}
                        onRevokePaymentRequest={(
                            paymentRequestUuid,
                            merchantOrganizationUuid
                        ) => {
                            if (props.revokePaymentRequestLink === undefined) {
                                throw new Error(
                                    'revokePaymentRequestLink is required for payment-request token actions'
                                );
                            }
                            void props.revokePaymentRequestLink(
                                paymentRequestUuid,
                                merchantOrganizationUuid
                            ).then(
                                async (response) => {
                                    setOperationErrorMessage(null);
                                    setOperationMessage(response.message);
                                    await queryClient.invalidateQueries({
                                        queryKey: props.queryKey,
                                    });
                                },
                                (error: unknown) => {
                                    setOperationMessage(null);
                                    if (error instanceof Error) {
                                        setOperationErrorMessage(error.message);
                                    } else {
                                        setOperationErrorMessage(
                                            'Failed to revoke payment link'
                                        );
                                    }
                                }
                            );
                        }}
                        onResend={(singleUseAuthTokenUuid) => {
                            resendMutation.mutate(singleUseAuthTokenUuid);
                        }}
                    />
                )}
            </CardContent>
        </Card>
    );
}
